import { hasPermissions } from '@common/utils/validation/authValidators'
import { TokenPermission } from '@data/rbac/model/rbac'

// Deliberately free of React and API imports so the rules can be unit-tested directly.
// The hook that reads the store and current user lives in `useValveEditPermissions.ts`.

export interface ValveEditPermissions {
  // Valve name — the only field a non-admin can change.
  canEditName: boolean
  // Manufacturer and size — physical facts about the installation.
  canEditHardware: boolean
}

// Narrowed to the fields the decision needs, so callers and tests need not build a Device.
export interface ValveEditDevice {
  accountId: string
  deviceLocationId: string
}

export interface ValveEditUser {
  adminAccountIds?: string[]
  adminCustomerLocationIds?: string[]
}

// Mirrors what the API enforces on `PUT /devices/{dUuid}/valve_settings`:
//
//   UPDATE:DEVICE:PROD:ADMIN — name, manufacturer, and size on any device.
//   UPDATE:DEVICE:PROD:USER  — name only, and only on devices in an account or location
//                              the user administers.
//
// The account/location check applies to the USER scope alone. ADMIN is a global device
// scope and deliberately bypasses it — the same split `DeviceInfoTab` already applies to
// device fields via `deviceAdmin` / `userUpdateFields`.
export const resolveValveEditPermissions = (
  permissions: TokenPermission[],
  user: ValveEditUser | undefined,
  device: ValveEditDevice,
): ValveEditPermissions => {
  const isDeviceAdmin = hasPermissions(permissions, ['UPDATE:DEVICE:PROD:ADMIN'])

  const administersDevice =
    !!user?.adminCustomerLocationIds?.includes(device.deviceLocationId) ||
    !!user?.adminAccountIds?.includes(device.accountId)

  const isLocalDeviceUser =
    hasPermissions(permissions, ['UPDATE:DEVICE:PROD:USER']) && administersDevice

  return {
    canEditName: isDeviceAdmin || isLocalDeviceUser,
    canEditHardware: isDeviceAdmin,
  }
}
